/**
 * Current-user role context. Fetches the signed-in user's Benchmark
 * `Practitioner` and the full set of their active `PractitionerRole`s once,
 * and exposes them — plus derived business role codes and role-predicate
 * helpers — to the entire authenticated React tree.
 *
 * This is the single source of truth for "who is the current user and what
 * roles do they hold", consolidating what was previously a per-call-site fetch
 * (`useCurrentPractitionerRole`, plus inline searches in TasksPage /
 * PatientsPage). It is the foundation the upcoming RBAC work builds on:
 * conditional rendering can read `roleCodes` / `hasRole(...)` from anywhere
 * without issuing its own network request.
 *
 * Mounted inside `<AuthenticatedLayout>` so it sits below `<MedplumProvider>`
 * (it calls `useMedplum()` / `useMedplumProfile()`) and only runs for
 * authenticated staff users.
 *
 * @agentic connections
 */

// Config
import { CONFIG } from "@/app/config";

// Constants & services
import {
  getPractitionerRolesBusinessCodes,
  practitionerRolesIncludeIctProfessional,
  type PractitionerRoleBusinessCode,
} from "@benchmark/core/constants";
import { fetchBenchmarkPractitionerRoles } from "@benchmark/core/services";

// Hooks
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";

// Types
import type { Practitioner, PractitionerRole } from "@medplum/fhirtypes";

/**
 * Current-user identity and role context exposed to consumers.
 * @agentic connections
 */
export type CurrentUser = {
  /** The signed-in user's Practitioner profile, or undefined while loading / signed out. */
  practitioner: Practitioner | undefined;
  /** All active Benchmark PractitionerRoles for the user, most recently updated first. */
  practitionerRoles: PractitionerRole[];
  /** The primary (most recently updated) role; matches legacy single-role semantics. */
  primaryPractitionerRole: PractitionerRole | undefined;
  /** De-duplicated known business role codes across all of the user's roles. */
  roleCodes: PractitionerRoleBusinessCode[];
  /** True while the role set is being fetched. */
  isLoading: boolean;
  /** False until the first role fetch completes for the signed-in practitioner. */
  isRolesResolved: boolean;
  /** Last fetch error, if any. */
  error: Error | null;
  /** Re-fetches the user's roles. */
  refetch: () => Promise<void>;
  /** True when the user holds the given business role. */
  hasRole: (code: PractitionerRoleBusinessCode) => boolean;
  /** True when the user holds at least one of the given business roles. */
  hasAnyRole: (codes: PractitionerRoleBusinessCode[]) => boolean;
  /** True when the user holds every one of the given business roles. */
  hasAllRoles: (codes: PractitionerRoleBusinessCode[]) => boolean;
  /** True when any active role carries the HL7 `ict` practitioner-role code. */
  isIctProfessional: boolean;
};

/** React context for the current user's role information. @agentic connections */
const CurrentUserContext = createContext<CurrentUser | null>(null);

/**
 * Reads the current-user role context. Throws if invoked outside
 * `<CurrentUserProvider>`.
 * @agentic connections
 */
export const useCurrentUser = (): CurrentUser => {
  const value = useContext(CurrentUserContext);
  if (!value) {
    throw new Error("useCurrentUser must be used within a CurrentUserProvider");
  }
  return value;
};

/**
 * Reads the current-user role context if present, otherwise returns null.
 * Lets shared hooks opt into the shared fetch when mounted under the provider
 * while remaining usable (with their own fallback) when it is not.
 * @agentic connections
 */
export const useCurrentUserOptional = (): CurrentUser | null =>
  useContext(CurrentUserContext);

/**
 * Provides the current user's Practitioner and PractitionerRole context to the
 * authenticated tree. Must be mounted inside `<MedplumProvider>`.
 * @agentic connections
 */
export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const medplum = useMedplum();
  const practitioner = useMedplumProfile() as Practitioner | undefined;

  const [practitionerRoles, setPractitionerRoles] = useState<
    PractitionerRole[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resolvedPractitionerId, setResolvedPractitionerId] = useState<
    string | undefined
  >(undefined);
  const [error, setError] = useState<Error | null>(null);

  const practitionerId = practitioner?.id;
  const isRolesResolved =
    practitionerId === undefined || resolvedPractitionerId === practitionerId;

  // Monotonic request token. Each fetch claims the next id; only the most
  // recent in-flight request is allowed to commit its result. This discards
  // out-of-order responses (e.g. after an account switch) that would otherwise
  // overwrite the context with a stale user's roles.
  const latestRequestId = useRef(0);

  const fetchRoles = useCallback(async () => {
    if (!practitionerId) {
      // Don't wipe previously-loaded roles when the profile is briefly
      // undefined during the SDK's token-refresh window (RequireAuth keeps the
      // tree mounted). On a real logout the provider unmounts and this state is
      // discarded anyway, so there is nothing to clear here.
      return;
    }

    const requestId = (latestRequestId.current += 1);
    setIsLoading(true);
    setError(null);
    try {
      const roles = await fetchBenchmarkPractitionerRoles(
        medplum,
        practitionerId,
        { benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID },
      );
      if (requestId !== latestRequestId.current) return;
      setPractitionerRoles(roles);
    } catch (err) {
      if (requestId !== latestRequestId.current) return;
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
    } finally {
      if (requestId === latestRequestId.current) {
        setIsLoading(false);
        setResolvedPractitionerId(practitionerId);
      }
    }
  }, [medplum, practitionerId]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchRoles();
    });
  }, [fetchRoles]);

  const roleCodes = useMemo(
    () => getPractitionerRolesBusinessCodes(practitionerRoles),
    [practitionerRoles],
  );

  const isIctProfessional = useMemo(
    () => practitionerRolesIncludeIctProfessional(practitionerRoles),
    [practitionerRoles],
  );

  const hasRole = useCallback(
    (code: PractitionerRoleBusinessCode) => roleCodes.includes(code),
    [roleCodes],
  );

  const hasAnyRole = useCallback(
    (codes: PractitionerRoleBusinessCode[]) =>
      codes.some((code) => roleCodes.includes(code)),
    [roleCodes],
  );

  const hasAllRoles = useCallback(
    (codes: PractitionerRoleBusinessCode[]) =>
      codes.every((code) => roleCodes.includes(code)),
    [roleCodes],
  );

  const value = useMemo<CurrentUser>(
    () => ({
      practitioner,
      practitionerRoles,
      primaryPractitionerRole: practitionerRoles[0],
      roleCodes,
      isLoading,
      error,
      refetch: fetchRoles,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      isIctProfessional,
      isRolesResolved,
    }),
    [
      practitioner,
      practitionerRoles,
      roleCodes,
      isLoading,
      isRolesResolved,
      error,
      fetchRoles,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      isIctProfessional,
    ],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};
